import Button from '@/components/common/Button';
import { WeeeIcon } from '@/components/common/Icon';
import SharePopup from '@/components/common/SharePopup';
import useTranslation from 'next-translate/useTranslation';
import { FC, ReactElement, SyntheticEvent, cloneElement, memo, useCallback, useEffect, useRef } from 'react';
import emmit from '../helper/group-order-mitt';
import { commonClickExtend } from '@/tracks/useClickActionT2';
import { useGroupOrder } from '@/store/group-order';
import { twMerge } from 'tailwind-merge';
import { ShareInfoType } from '../types';

type InviteBtnProps = {
  isHost?: boolean; //是否团长, 团员  模式展示分2种
  shareInfo?: ShareInfoType;
  customButton?: ReactElement;
};

const _InviteBtn: FC<InviteBtnProps> = ({ isHost, shareInfo, customButton }) => {
  const { t } = useTranslation('groupOrder');
  const sharePopupRef: any = useRef(null);
  const { intervalGroupOrderData } = useGroupOrder();

  const { vendor_id, count_users, count_items, key, is_creator } = intervalGroupOrderData ?? {};

  const getData = useCallback(
    async (callback?) => {
      const queryParams = new URLSearchParams(window.location.search);
      //如果是发起人第一次进到商家拼团页面, 自动弹出分享弹窗, 每次拼团只弹一次
      const storedRroupOrderKey = localStorage.getItem('show_mkpl_group_share_pop_once') || '';
      if (isHost && !storedRroupOrderKey.includes(queryParams.get('key')) && location.pathname.includes('list-host')) {
        const timer = setTimeout(() => {
          if (timer) {
            clearTimeout(timer);
          }
          sharePopupRef?.current?.show();
          localStorage.setItem('show_mkpl_group_share_pop_once', storedRroupOrderKey + queryParams.get('key'));
        }, 1000);
      }
    },
    [isHost]
  );

  const inviteClickTrack = useCallback(() => {
    commonClickExtend({
      co: {
        target_nm: 'invite',
        target_type: 'normal_button',
        target_pos: 0,
        click_type: 'invite'
      },
      ctx: {
        global_vendor: vendor_id,
        count_users: count_users,
        count_items: count_items,
        is_creator: is_creator,
        group_order_id: key
      }
    });
  }, [count_items, count_users, is_creator, key, vendor_id]);

  const viewGroupTrack = () => {
    commonClickExtend({
      co: {
        target_nm: 'view_group_order',
        target_type: 'popup',
        click_type: 'view'
      },
      ctx: {
        global_vendor: intervalGroupOrderData?.vendor_id,
        count_users: intervalGroupOrderData?.count_users,
        count_items: intervalGroupOrderData?.count_items,
        is_creator: intervalGroupOrderData?.is_creator,
        group_order_id: intervalGroupOrderData?.key
      }
    });
  };

  const onClickButton = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      sharePopupRef.current?.show();
      inviteClickTrack();
    },
    [inviteClickTrack]
  );

  useEffect(() => {
    const handle = (isFirst?: boolean) => {
      if (isFirst) {
        getData();
      } else {
        sharePopupRef.current?.show();
      }
    };

    emmit.on('showShareGroupOrderPop', handle);

    return () => {
      emmit.off('showShareGroupOrderPop', handle);
    };
  }, [getData]);

  return (
    <>
      {!!customButton ? (
        cloneElement(customButton, {
          onClick: onClickButton
        })
      ) : (
        <div
          data-testid="btn-invite-friends"
          onClick={onClickButton}
          className={twMerge(
            'flex flex-row items-center justify-center flex-shrink-0',
            'box-border border-solid border border-[#ffffff66] rounded-full',
            ' h-9 p-2.5'
          )}>
          <WeeeIcon type="iconinvite1" className=" text-reserved-true-white mr-200" />
          <span className="enki-body-2xs-medium text-reserved-true-white">{t('invite_friends')}</span>
        </div>
      )}

      <SharePopup
        ref={sharePopupRef}
        closeShare={() => {
          // TODO:这里确认应该是不需要了
          // emmit.emit('groupOrderHide');
        }}
        onBeforeShow={show => {
          show();
        }}
        data={shareInfo}
        render={() => {
          return null;
        }}
        title={t('group_order_invite_title')}
        shareChannelClickTrackConfigs={{
          getTrackParams: () => {
            return {
              ctx: {
                global_vendor: intervalGroupOrderData?.vendor_id,
                count_users: intervalGroupOrderData?.count_users,
                count_items: intervalGroupOrderData?.count_items,
                is_creator: intervalGroupOrderData?.is_creator,
                group_order_id: intervalGroupOrderData?.key
              }
            };
          }
        }}
      />
    </>
  );
};

const InviteBtn = memo(_InviteBtn);

export default InviteBtn;
